import { Alert, Icon, IconButton } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormTemplateTypeEnum } from "../../_common/enum/forms/fomTemplateType.enum";
import { FormStatusEnum } from "../../_common/enum/forms/formStatus.enum";
import { IFilteringViewModel, ITableData } from "../../_common/interface/table";
import { CommonTable } from "../../_components/table/common.table";
import { PaperTableHeader } from "../../_components/table/header.table";
import { SearchTableHeader } from "../../_components/table/search.table";
import { TABLE_FILTER } from "../../_const/tableFilter.const";
import { getDateTime } from "../../_helpers/dateTime.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { formService } from "../../_services/form.service";
import { DataTypeEnum } from "../../_common/enum/dataType.enum";

function escapeHtmlCell(value: string | number | null | undefined): string {
    if (value === null || value === undefined) return "";
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function formStatusLabel(formStatusId: number): string {
    const name = FormStatusEnum[formStatusId];
    return typeof name === "string" ? name : String(formStatusId);
}

export function FormIndex(params: { formType?: FormTemplateTypeEnum, title: string, id: any }) {

    const [data, setData] = useState<ITableData<any>>({ items: [], count: 0 });
    const [isLoading, setLoading] = useState(true);
    const [filter, setFilter] = useState<IFilteringViewModel>({ ...TABLE_FILTER, id: params.id, formTemplateTypeId: Number(params.formType) });
    var url = params.formType == FormTemplateTypeEnum.Centre ? '/Centre/Details/' : params.formType == FormTemplateTypeEnum.Client ? '/Client/Details/' : '';

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                id: 'actions',
                header: 'Actions',
                size: 72,
                maxSize: 80,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }) => (<>
                    <IconButton
                        color="primary"
                        onClick={() => window.open(`${url}${params.id}/Forms/${row.original.id}`, "_blank")
                        }>
                        <Icon>edit</Icon>
                    </IconButton>
                </>
                ),
            },
            {
                accessorKey: 'name',
                header: 'Form name',
                size: 220,
                minSize: 140,
            },
            {
                accessorKey: 'topic',
                header: 'Topic',
                size: 160,
                minSize: 100,
            },
            {
                accessorKey: 'referenceNumber',
                header: 'Reference',
                size: 110,
                maxSize: 140,
            },
            {
                accessorKey: 'createdDate',
                header: 'Created',
                size: 132,
                maxSize: 150,
                accessorFn: (rowData: any) => <>{rowData.createdDate ? getDateTime(rowData.createdDate) : "-"}</>,
            },
            {
                accessorKey: 'modifiedDate',
                header: 'Modified',
                size: 132,
                maxSize: 150,
                accessorFn: (rowData: any) => <>{rowData.modifiedDate ? getDateTime(rowData.modifiedDate) : "-"}</>,
            },
            {
                accessorKey: 'modifiedBy',
                header: 'Modified By',
                size: 120,
                maxSize: 180,
            },
            {
                accessorKey: 'formStatusId',
                header: 'Form Status',
                size: 180,
                minSize: 140,
                accessorFn: (rowData: any) => <>
                    {
                        rowData.formStatusId == FormStatusEnum.New ? <Alert severity="info">{FormStatusEnum[rowData.formStatusId]}</Alert> :
                            rowData.formStatusId == FormStatusEnum.Approved ? <Alert severity="success">{FormStatusEnum[rowData.formStatusId]}</Alert> :
                                rowData.formStatusId == FormStatusEnum.InProcess ? <Alert severity="info">In Process</Alert> :
                                    rowData.formStatusId == FormStatusEnum.AwaitingApproval ? <Alert severity="warning">Awaiting Approval</Alert> :
                                        rowData.formStatusId == FormStatusEnum.Withdrawn ? <Alert severity="warning">{FormStatusEnum[rowData.formStatusId]}</Alert> :
                                            rowData.formStatusId == FormStatusEnum.Cancelled ? <Alert severity="error">{FormStatusEnum[rowData.formStatusId]}</Alert> :
                                                <></>}
                </>,
            },

        ],
        [],
    );

    const fetchData = useCallback(() => {
        setLoading(true);
        return formService.getAll({ ...filter })
            .then(
                (d) => {
                    setData(d);
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }, [filter]);

    useEffect(() => {
        if (filter) fetchData();
    }, [filter, fetchData]);

    const handlePrint = useCallback(() => {
        const items = data.items || [];
        const title = params.title ? `${params.title} — Forms` : "Forms";
        let rows = "";
        for (const row of items) {
            const created = row.createdDate ? getDateTime(row.createdDate) : "-";
            const modified = row.modifiedDate ? getDateTime(row.modifiedDate) : "-";
            rows += "<tr>";
            rows += `<td>${escapeHtmlCell(row.name)}</td>`;
            rows += `<td>${escapeHtmlCell(row.topic)}</td>`;
            rows += `<td>${escapeHtmlCell(row.referenceNumber)}</td>`;
            rows += `<td>${escapeHtmlCell(created)}</td>`;
            rows += `<td>${escapeHtmlCell(modified)}</td>`;
            rows += `<td>${escapeHtmlCell(row.modifiedBy)}</td>`;
            rows += `<td>${escapeHtmlCell(formStatusLabel(row.formStatusId))}</td>`;
            rows += "</tr>";
        }
        const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${escapeHtmlCell(title)}</title>
<style>
  body { font-family: system-ui, sans-serif; padding: 16px; }
  h1 { font-size: 1.25rem; margin: 0 0 12px; }
  table { border-collapse: collapse; width: 100%; font-size: 12px; }
  th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f5f5f5; }
</style></head><body>
<h1>${escapeHtmlCell(title)}</h1>
<table>
<thead><tr>
<th>Form name</th><th>Topic</th><th>Reference</th><th>Created</th><th>Modified</th><th>Modified By</th><th>Form Status</th>
</tr></thead>
<tbody>${rows || '<tr><td colspan="7">No rows</td></tr>'}</tbody>
</table>
<script>window.onload = function() { window.focus(); window.print(); };</script>
</body></html>`;
        // Blob URL so the tab loads content without document.write (Firefox returns null for noopener + blank open)
        const printUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
        const printWindow = window.open(printUrl, "_blank", "noopener,noreferrer");
        if (printWindow) printWindow.opener = null;
        setTimeout(() => URL.revokeObjectURL(printUrl), 60_000);
    }, [data.items, params.title]);

    return (
        <>

            <PaperTableHeader
                title="Forms"
                buttons={[{ icon: "print", label: "Print", onClick: handlePrint }]}
            />

            <SearchTableHeader inputs={[
                { label: "Form Type", name: "formTemplateId", type: "autocomplete", dataType: DataTypeEnum.FormTemplate, parentId: params.formType, onChange: setFilter, value: filter.formTemplateId, width: 250 },
                { label: "From Date", name: "fromDate", type: "date", onChange: setFilter, value: filter.fromDate },
                { label: "To Date", name: "toDate", type: "date", onChange: setFilter, value: filter.toDate },
            ]} />

            <CommonTable
                columns={columns}
                data={data}
                filter={filter}
                setFilter={setFilter}
                loading={isLoading}
            />

        </>
    );

}
